import {
  computed,
  inject,
  Injectable,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { LOGGING_SERVICE_TOKEN } from '@aksia/infrastructure';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { SIGNALR_CONFIG_TOKEN } from './signalr.tokens';
import {
  SignalrBroadcast,
  SignalrConnectionState,
  SignalrHubConnectionInfo,
  SignalrMessageEnvelope,
  SignalrMessageHandler,
  SignalrRawEnvelope,
  SignalrSubscription,
} from './signalr.types';

const MESSAGE_QUEUED = 'MessageQueued';

interface HubEntry {
  hubId: string;
  connection: HubConnection | null;
  clientId: string | null;
  connectionState: WritableSignal<SignalrConnectionState>;
  lastError: WritableSignal<unknown | null>;
  messageHandlers: Map<string, Set<SignalrMessageHandler>>;
  messageQueuedRegistered: boolean;
  connectingPromise: Promise<void> | null;
}

/**
 * Manages SignalR connections to Azure SignalR Service via Azure Functions.
 *
 * Connection flow per `hubId`:
 * 1. POST `{ HubId }` to `config.configUrl` with `x-functions-key` header.
 * 2. The function returns `{ hubUrl, accessToken, clientId }`.
 * 3. Open a WebSocket directly against `hubUrl` using `accessToken` —
 *    `skipNegotiation: true` bypasses the SDK's own negotiate hop.
 * 4. Listen on the `MessageQueued` hub method for `SignalrBroadcast` payloads.
 *    Filter out broadcasts where `triggerClientId === clientId` (own messages).
 *    Parse `broadcast.message` → `{ type, serializedPayload }`, then parse
 *    `serializedPayload` to get the typed payload, and dispatch by `type`.
 *
 * Each `hubId` is a separate connection — there's no group concept here.
 */
@Injectable({ providedIn: 'root' })
export class SignalrService {
  private readonly config = inject(SIGNALR_CONFIG_TOKEN, { optional: true });
  private readonly logger = inject(LOGGING_SERVICE_TOKEN, { optional: true });
  private readonly disabled: boolean;
  private readonly hubs = new Map<string, HubEntry>();

  constructor() {
    this.disabled = this.computeDisabled();
  }

  // -------- public read API --------

  getConnectionState(hubId: string): Signal<SignalrConnectionState> {
    return this.getOrCreateHub(hubId).connectionState.asReadonly();
  }

  isHubConnected(hubId: string): Signal<boolean> {
    const hub = this.getOrCreateHub(hubId);
    return computed(() => hub.connectionState() === 'Connected');
  }

  getLastError(hubId: string): Signal<unknown | null> {
    return this.getOrCreateHub(hubId).lastError.asReadonly();
  }

  getClientId(hubId: string): string | null {
    return this.hubs.get(hubId)?.clientId ?? null;
  }

  // -------- connection lifecycle --------

  async connectToHub(hubId: string): Promise<void> {
    if (this.disabled) return;
    const hub = this.getOrCreateHub(hubId);
    if (hub.connection?.state === HubConnectionState.Connected) return;
    if (hub.connectingPromise) return hub.connectingPromise;

    hub.connectingPromise = (async () => {
      try {
        hub.connectionState.set('Connecting');
        const info = await this.fetchHubConnectionInfo(hubId);
        hub.clientId = info.clientId;
        hub.connection = this.buildConnection(info);
        this.wireLifecycle(hub);
        this.ensureMessageQueuedRegistered(hub);
        await hub.connection.start();
        hub.connectionState.set('Connected');
      } catch (err) {
        hub.lastError.set(err);
        hub.connectionState.set('Disconnected');
        this.logger?.handleError({
          title: `SignalR connect failed for hub "${hubId}"`,
          message: (err as Error)?.message ?? 'Unknown',
          extra: { error: err },
        });
        throw err;
      } finally {
        hub.connectingPromise = null;
      }
    })();

    return hub.connectingPromise;
  }

  async disconnectHub(hubId: string): Promise<void> {
    const hub = this.hubs.get(hubId);
    if (!hub?.connection) return;
    try {
      await hub.connection.stop();
    } finally {
      hub.connection = null;
      hub.clientId = null;
      hub.messageQueuedRegistered = false;
      hub.connectionState.set(this.disabled ? 'Disabled' : 'Disconnected');
    }
  }

  async disconnectAll(): Promise<void> {
    await Promise.all(
      [...this.hubs.keys()].map((id) => this.disconnectHub(id)),
    );
  }

  // -------- message subscription --------

  onMessage<TPayload = unknown>(
    hubId: string,
    messageType: string,
    handler: SignalrMessageHandler<TPayload>,
  ): SignalrSubscription {
    if (this.disabled) {
      return { hubId, channel: messageType, off: () => undefined };
    }

    const hub = this.getOrCreateHub(hubId);
    let bucket = hub.messageHandlers.get(messageType);
    if (!bucket) {
      bucket = new Set<SignalrMessageHandler>();
      hub.messageHandlers.set(messageType, bucket);
    }
    bucket.add(handler as SignalrMessageHandler);

    if (hub.connection) {
      this.ensureMessageQueuedRegistered(hub);
    }

    void this.connectToHub(hubId);

    return {
      hubId,
      channel: messageType,
      off: () =>
        this.offMessage(hubId, messageType, handler as SignalrMessageHandler),
    };
  }

  offMessage(
    hubId: string,
    messageType: string,
    handler?: SignalrMessageHandler,
  ): void {
    const hub = this.hubs.get(hubId);
    if (!hub) return;
    const bucket = hub.messageHandlers.get(messageType);
    if (!bucket) return;
    if (handler) bucket.delete(handler);
    else bucket.clear();
    if (bucket.size === 0) hub.messageHandlers.delete(messageType);
  }

  // -------- request/response --------

  async invokeOnHub<T = unknown>(
    hubId: string,
    method: string,
    ...args: unknown[]
  ): Promise<T> {
    if (this.disabled) return undefined as T;
    await this.connectToHub(hubId);
    const hub = this.hubs.get(hubId)!;
    return (await hub.connection!.invoke(method, ...args)) as T;
  }

  async sendOnHub(
    hubId: string,
    method: string,
    ...args: unknown[]
  ): Promise<void> {
    if (this.disabled) return;
    await this.connectToHub(hubId);
    const hub = this.hubs.get(hubId)!;
    await hub.connection!.send(method, ...args);
  }

  // -------- internals --------

  private getOrCreateHub(hubId: string): HubEntry {
    let hub = this.hubs.get(hubId);
    if (!hub) {
      hub = {
        hubId,
        connection: null,
        clientId: null,
        connectionState: signal<SignalrConnectionState>(
          this.disabled ? 'Disabled' : 'Disconnected',
        ),
        lastError: signal<unknown | null>(null),
        messageHandlers: new Map(),
        messageQueuedRegistered: false,
        connectingPromise: null,
      };
      this.hubs.set(hubId, hub);
    }
    return hub;
  }

  private async fetchHubConnectionInfo(
    hubId: string,
  ): Promise<SignalrHubConnectionInfo> {
    const cfg = this.config!;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (cfg.functionsKey) headers['x-functions-key'] = cfg.functionsKey;

    const res = await fetch(cfg.configUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({ HubId: hubId }),
    });
    if (!res.ok) {
      throw new Error(
        `GetSignalRConfiguration failed: ${res.status} ${res.statusText}`,
      );
    }
    return (await res.json()) as SignalrHubConnectionInfo;
  }

  private buildConnection(info: SignalrHubConnectionInfo): HubConnection {
    return new HubConnectionBuilder()
      .withUrl(info.hubUrl, {
        accessTokenFactory: () => info.accessToken,
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();
  }

  private wireLifecycle(hub: HubEntry): void {
    const conn = hub.connection!;
    conn.onreconnecting((err) => {
      if (err) hub.lastError.set(err);
      hub.connectionState.set('Reconnecting');
    });
    conn.onreconnected(() => {
      hub.connectionState.set('Connected');
    });
    conn.onclose((err) => {
      if (err) hub.lastError.set(err);
      hub.connectionState.set(this.disabled ? 'Disabled' : 'Disconnected');
    });
  }

  private ensureMessageQueuedRegistered(hub: HubEntry): void {
    if (hub.messageQueuedRegistered || !hub.connection) return;
    hub.connection.on(MESSAGE_QUEUED, (raw: unknown) =>
      this.dispatchMessage(hub, raw),
    );
    hub.messageQueuedRegistered = true;
  }

  private dispatchMessage(hub: HubEntry, raw: unknown): void {
    let broadcast: SignalrBroadcast;
    try {
      broadcast = (
        typeof raw === 'string' ? JSON.parse(raw) : raw
      ) as SignalrBroadcast;
    } catch (err) {
      this.logger?.handleError({
        title: 'SignalR broadcast parse error',
        message: (err as Error)?.message ?? 'parse failed',
        extra: { error: err },
      });
      return;
    }

    if (!broadcast?.message) return;

    let rawEnvelope: SignalrRawEnvelope;
    try {
      rawEnvelope = JSON.parse(broadcast.message) as SignalrRawEnvelope;
    } catch (err) {
      this.logger?.handleError({
        title: 'SignalR envelope parse error',
        message: (err as Error)?.message ?? 'parse failed',
        extra: { error: err },
      });
      return;
    }

    // Normalize PascalCase wire shape → camelCase handler API.
    // `clientId` / `triggerClientId` are stamped from the connection and the
    // outer broadcast so handlers can opt-in to filter own broadcasts (the
    // service does not filter — backend `clientId` is user-scoped, so a
    // service-level filter would suppress legitimate cross-tab updates).
    const envelope: SignalrMessageEnvelope = {
      type: rawEnvelope?.Type,
      serializedPayload: rawEnvelope?.SerializedPayload,
      hubKey: rawEnvelope?.HubKey,
      hubMethod: rawEnvelope?.HubMethod,
      audience: rawEnvelope?.Audience,
      id: rawEnvelope?.Id,
      timestamp: rawEnvelope?.Timestamp,
      clientId: hub.clientId ?? undefined,
      triggerClientId: broadcast.triggerClientId,
    };

    const bucket = hub.messageHandlers.get(envelope.type);
    if (!bucket || bucket.size === 0) return;

    let payload: unknown = undefined;
    if (envelope.serializedPayload) {
      try {
        payload = JSON.parse(envelope.serializedPayload);
      } catch (err) {
        this.logger?.handleError({
          title: `SignalR payload parse error for "${envelope.type}"`,
          message: (err as Error)?.message ?? 'parse failed',
          extra: { error: err },
        });
        return;
      }
    }

    for (const handler of bucket) {
      try {
        handler(payload, envelope);
      } catch (err) {
        this.logger?.handleError({
          title: `SignalR message handler "${envelope.type}"`,
          message: (err as Error)?.message ?? 'handler threw',
          extra: { error: err },
        });
      }
    }
  }

  private computeDisabled(): boolean {
    if (!this.config) return true;
    if (this.config.autoConnect === false) return true;
    if (!this.config.configUrl) return true;
    return false;
  }
}
